import {
  FinalizationItemModel,
  FinalizationStatus,
  DocumentModel,
  PageableModel,
  TransactionModel,
  SettlementDetailModel,
} from "@/models/FinalizationModel";
import {
  SettlementResponse,
  SettlementDetailResponse,
  PageResponseSettlementResponse,
  DetailSettlementTransResponse,
  SettlementResponseStatusEnum,
  TransactionResponse,
  PageResponseTransactionResponse,
} from "@/api/generated-sources/kienlong-pay/api";

/**
 * Adapter để transform data từ API response sang Domain Model
 */

/**
 * Map SettlementResponseStatusEnum → FinalizationStatus
 */
function mapStatus(status?: SettlementResponseStatusEnum): FinalizationStatus {
  switch (status) {
    case SettlementResponseStatusEnum.Success:
      return FinalizationStatus.SUCCESS;
    case SettlementResponseStatusEnum.Pending:
      return FinalizationStatus.PENDING;
    case SettlementResponseStatusEnum.Cancelled:
      return FinalizationStatus.CANCELLED;
    case SettlementResponseStatusEnum.Fail:
      return FinalizationStatus.FAIL;
    case SettlementResponseStatusEnum.CutOver:
      return FinalizationStatus.CUT_OVER;
    default:
      return FinalizationStatus.PENDING;
  }
}

/**
 * Map SettlementResponse → FinalizationItemModel
 *
 * @param dto - Raw data từ API backend
 * @returns FinalizationItemModel
 */
export function mapFinalizationItemModel(
  dto: SettlementResponse,
): FinalizationItemModel {
  return {
    id: dto.settlementId || "",
    code: dto.settlementCode || "",
    fromDate: dto.startDate || "",
    toDate: dto.endDate || "",
    companyId: dto.merchantId || "",
    companyName: dto.merchantName || "",
    totalRevenue: dto.totalRevenue || 0,
    tax: dto.totalVatChargeAmount || 0,
    serviceFeeHold: dto.totalChargeAmount || 0,
    paymentTransferAmount: dto.totalActualAmount || 0,
    totalTransaction: dto.totalTransaction || 0,
    status: mapStatus(dto.status),
    createBy: dto.settlementBy,
    createDate: dto.settlementDateTime,
    updateBy: dto.settlementBy,
    updateDate: dto.settlementDateTime,
    documents: [], // API không trả về documents trong list
  };
}

/**
 * Map DetailSettlementTransResponse → SettlementDetailModel
 *
 * @param dto - Raw data từ API backend
 * @returns SettlementDetailModel
 */
export function mapFinalizationDetailModel(
  dto: DetailSettlementTransResponse,
): SettlementDetailModel {
  const base: FinalizationItemModel = {
    id: dto.settlementId || "",
    code: dto.settlementCode || "",
    fromDate: dto.startDate || "",
    toDate: dto.endDate || "",
    companyId: dto.merchantId || "",
    companyName: dto.merchantName || "",
    totalRevenue: dto.totalRevenue || 0,
    tax: dto.totalVatChargeAmount || 0,
    serviceFeeHold: dto.totalChargeAmount || 0,
    paymentTransferAmount: dto.totalActualAmount || 0,
    totalTransaction: dto.totalTransaction || 0,
    status: mapStatus(dto.status),
    createBy: dto.settlementBy,
    createDate: dto.settlementDateTime,
    updateBy: dto.settlementBy,
    updateDate: dto.settlementDateTime,
    documents: [], // API không trả về documents trong detail
  };

  // Map transactions nếu có
  let transactions: PageableModel<TransactionModel> | undefined;
  if (dto.transactions) {
    transactions = mapTransactionListResponse(dto.transactions);
  }

  return {
    ...base,
    transactions,
  };
}

/**
 * Map TransactionResponse → TransactionModel
 */
export function mapTransactionModel(
  dto: TransactionResponse,
): TransactionModel {
  return {
    id: dto.id || "",
    refTransactionId: dto.refTransactionId,
    txnNumber: dto.txnNumber,
    status: dto.status,
    statusVi: dto.statusVi,
    statusViPortal: dto.statusViPortal,
    amount: dto.amount || 0,
    chargeAmount: dto.chargeAmount,
    vatChargeAmount: dto.vatChargeAmount,
    actualAmount: dto.actualAmount,
    createDateTime: dto.createDateTime,
    completeTime: dto.completeTime,
    virtualAccount: dto.virtualAccount,
    description: dto.description,
    paymentType: dto.paymentType,
    customerName: dto.customerName,
    customerPhone: dto.customerPhone,
    customerEmail: dto.customerEmail,
    customerAddress: dto.customerAddress,
    payLinkCode: dto.payLinkCode,
    payPageCode: dto.payPageCode,
    accountNo: dto.accountNo,
    accountName: dto.accountName,
    interBankTrace: dto.interBankTrace,
    forControl: dto.forControl,
  };
}

/**
 * Map PageResponseTransactionResponse → PageableModel<TransactionModel>
 */
export function mapTransactionListResponse(
  dto: PageResponseTransactionResponse,
): PageableModel<TransactionModel> {
  return {
    items: (dto.items || []).map(mapTransactionModel),
    pageNumber: dto.pageNumber || 0,
    pageSize: dto.pageSize || 10,
    totalSize: dto.totalSize || 0,
    totalPage: dto.totalPage || 0,
  };
}

/**
 * Map Document DTO → DocumentModel
 * (Nếu API có trả về documents trong tương lai)
 */
export function mapDocumentModel(dto: any): DocumentModel {
  return {
    id: dto.id || "",
    fileName: dto.fileName || "",
    fileUrl: dto.fileUrl || "",
    uploadDate: dto.uploadDate || "",
    uploadBy: dto.uploadBy || "",
    fileSize: dto.fileSize || 0,
    fileType: dto.fileType || "",
  };
}

/**
 * Map list of DTOs → list of Models
 */
export function mapFinalizationList(
  dtos: SettlementResponse[],
): FinalizationItemModel[] {
  return dtos.map(mapFinalizationItemModel);
}

/**
 * Map pageable response từ API
 */
export function mapFinalizationListResponse(
  dto: PageResponseSettlementResponse,
): PageableModel<FinalizationItemModel> {
  return {
    items: (dto.items || []).map(mapFinalizationItemModel),
    pageNumber: dto.number || 0,
    pageSize: dto.size || 10,
    totalSize: dto.totalElements || 0,
    totalPage: dto.totalPages || 0,
  };
}
